import React from "react";

// ─────────────────────────────────────────────────────────
// GJBWReportCard — Black & White, Print-Friendly Report Card
// ─────────────────────────────────────────────────────────
// Premium, highly graphical 100% black & white marksheet design
// tailored strictly to fit a single A4 page with optimal vertical rhythm.
// Features:
//  - Authentic certificate double-border frame with clean corner geometry
//  - Balanced header with Institute Logo + Centered Branding + Right Session Badge
//  - High-contrast inverted table headers (#000 background, #fff text)
//  - Spacious, clearly defined Subject Marks Table
//  - 4 Uniform Academic KPI Metric Cards (including matching Overall Grade card)
//  - Full-width structured Grade Classification Matrix
//  - Official 3-Point Signatures and GradeMe AI Footer Branding
// ─────────────────────────────────────────────────────────

const getGrade = (pct, mapping) => {
  if (!mapping || mapping.length === 0) return { grade: "—", remark: "" };
  const sorted = [...mapping].sort(
    (a, b) => b.minPercentage - a.minPercentage,
  );
  const match = sorted.find((m) => pct >= m.minPercentage);
  return match
    ? { grade: match.grade, remark: match.remark }
    : { grade: "—", remark: "" };
};

/**
 * Clean a token of outer punctuation (parentheses, quotes, trailing dots, etc.)
 */
const cleanToken = (str) => {
  if (!str) return "";
  const cleaned = str.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, "");
  return cleaned || str;
};

/**
 * Score a potential (std, grp) pair to select the best match if multiple dashes exist.
 */
const scoreMatch = (stdRaw, grpRaw) => {
  let score = 0;
  const s = cleanToken(stdRaw);
  const g = cleanToken(grpRaw);

  // Standard heuristics (e.g. 1-12, 10th, 11th, Roman I-XII)
  if (/^(?:[1-9]|1[0-5])(?:st|nd|rd|th)?$/i.test(s) || /^(?:I|II|III|IV|V|VI|VII|VIII|IX|X|XI|XII)$/i.test(s)) {
    score += 10;
  } else if (/^\d+$/.test(s) && !/^(?:19|20)\d{2}$/.test(s)) {
    score += 5;
  }

  // Group heuristics (single letter like A, B, C or section code like A1, Commerce, Science)
  if (/^[A-Za-z]$/.test(g)) {
    score += 10;
  } else if (/^[A-Za-z][0-9]$/.test(g) || /^(?:Science|Commerce|Arts|General|Bio|Math)$/i.test(g)) {
    score += 8;
  }

  // Penalize prefixes like Grade, Class, Std
  if (/^(?:Grade|Class|Std|Standard|Year|Session)$/i.test(s)) {
    score -= 10;
  }
  // Penalize 4-digit calendar year
  if (/^(?:19|20)\d{2}$/.test(s) || /^(?:19|20)\d{2}$/.test(g)) {
    score -= 15;
  }

  return score;
};

/**
 * Parse a level string into { std, group }.
 *
 * Rules:
 *  1. Strict Hyphen Standard (Group strictly requires '-'):
 *     - If '-' is present (e.g. "Grade 11-A English Medium", "Grade 11 - A", "11-A"):
 *       - Only one word immediately before '-' is STD (e.g. "11")
 *       - Only one word immediately after '-' is GROUP (e.g. "A")
 *       - Surrounding words ("Grade", "English Medium", etc.) are completely ignored.
 *
 *  2. No Hyphen => No Group:
 *     - If no '-' is present (e.g. "Grade 11 English Medium", "Class 10", "11", "Nursery"):
 *       - Group is strictly "" (GROUP column is not displayed).
 *       - Extracts standard number (e.g. "11", "10") or label ("Nursery").
 */
const parseLevel = (level) => {
  if (!level) return { std: "—", group: "" };
  const trimmed = String(level).trim();
  if (!trimmed) return { std: "—", group: "" };

  // ── RULE 1: Strict Hyphen-based pattern (A '-' is required for Group) ──
  const dashes = [...trimmed.matchAll(/[-–—]/g)];
  if (dashes.length > 0) {
    const candidates = [];
    for (const d of dashes) {
      const idx = d.index;
      const beforePart = trimmed.slice(0, idx);
      const afterPart = trimmed.slice(idx + 1);
      const beforeMatch = beforePart.match(/([^\s–—-]+)\s*$/);
      const afterMatch = afterPart.match(/^\s*([^\s–—-]+)/);
      if (beforeMatch && afterMatch) {
        const stdRaw = beforeMatch[1];
        const grpRaw = afterMatch[1];
        candidates.push({
          std: cleanToken(stdRaw),
          group: cleanToken(grpRaw),
          score: scoreMatch(stdRaw, grpRaw),
        });
      }
    }

    if (candidates.length > 0) {
      candidates.sort((a, b) => b.score - a.score);
      const best = candidates[0];
      if (best.std) {
        return { std: best.std, group: best.group };
      }
    }
  }

  // ── RULE 2: No hyphen => NO Group (Extract STD only) ──
  // E.g. "Grade 11 English Medium", "Class 10 Commerce", "Grade 10"
  const standardNumberMatch = trimmed.match(
    /(?:^|\s)(?:Grade|Class|Std|Standard)?\s*(\d+|[IVXLCDM]+|[0-9]+th|[0-9]+st|[0-9]+nd|[0-9]+rd)(?:\s+|$)/i
  );
  if (standardNumberMatch) {
    return { std: cleanToken(standardNumberMatch[1]), group: "" };
  }

  // Fallback for non-numeric levels (e.g. "Nursery", "Playgroup")
  const stripped = trimmed.replace(/^(?:Grade|Class|Standard|STD)\s+/i, "");
  return { std: stripped || trimmed, group: "" };
};

const GJBWReportCard = ({
  instituteInfo,
  studentName,
  referenceId,
  rollNo,
  level,
  periodLabel,
  examinations = [],
  cumulativeScore,
  cumulativeMaxScore,
  cumulativePct,
  rank,
  totalStudents,
  gradeLetterMapping = [],
}) => {
  const instituteName =
    instituteInfo?.name || instituteInfo?.instituteName || "GradeMe AI";
  const instituteTagline =
    instituteInfo?.tagline || instituteInfo?.description || "";
  const instituteAddress =
    instituteInfo?.address || "";
  const instituteLogo =
    instituteInfo?.logoURL ||
    "https://res.cloudinary.com/dfdsncmih/image/upload/v1761210947/grademe_ai_logo_crp_vrjuhf.png";

  const { std, group } = parseLevel(level);

  // Count total subjects (union across all exams)
  const subjectSet = new Set();
  examinations.forEach((exam) =>
    (exam.subjects || []).forEach((s) => subjectSet.add(s.className)),
  );

  // Build cumulative per-subject data
  const cumulativeBySubject = {};
  examinations.forEach((exam) => {
    (exam.subjects || []).forEach((subj) => {
      const key = subj.className;
      if (!cumulativeBySubject[key]) {
        cumulativeBySubject[key] = { className: key, score: 0, maxScore: 0 };
      }
      cumulativeBySubject[key].maxScore += subj.maxScore || 0;
      if (!subj.absent) {
        cumulativeBySubject[key].score += subj.score || 0;
      }
    });
  });
  const cumulativeSubjects = Object.values(cumulativeBySubject).map((s) => ({
    ...s,
    percentage: s.maxScore > 0 ? (s.score / s.maxScore) * 100 : 0,
  }));

  // Determine overall pass/fail
  const overallPct = cumulativePct || 0;
  const isPass = overallPct >= (instituteInfo?.passPercentage || 33);
  const resultText = isPass ? "PASS" : "FAIL";

  // Grade for overall
  const overallGradeObj = getGrade(overallPct, gradeLetterMapping);
  const hasGrades = gradeLetterMapping.length > 0;

  // Formatted current date for report issuance
  const formattedDate = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  // Calculate primary exam totals
  const primaryExam = examinations[0] || {};
  const subjectsList = primaryExam.subjects || cumulativeSubjects || [];
  const totalMaxMarks =
    primaryExam.totalMaxScore || cumulativeMaxScore || 0;
  const totalObtainedMarks =
    primaryExam.totalScore !== undefined
      ? primaryExam.totalScore
      : cumulativeScore || 0;

  // Standard grade list if mapping not provided
  const effectiveGradeScale =
    gradeLetterMapping.length > 0
      ? gradeLetterMapping
      : [
          { grade: "A1", minPercentage: 90, remark: "Outstanding" },
          { grade: "A2", minPercentage: 80, remark: "Excellent" },
          { grade: "B1", minPercentage: 70, remark: "Very Good" },
          { grade: "B2", minPercentage: 60, remark: "Good" },
          { grade: "C1", minPercentage: 50, remark: "Satisfactory" },
          { grade: "C2", minPercentage: 40, remark: "Needs Improvement" },
        ];

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "794px",
        height: "1123px",
        margin: "0 auto",
        padding: "6px",
        background: "#ffffff",
        border: "3px solid #000000",
        boxSizing: "border-box",
        fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
        color: "#000000",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* ── Inner Frame with Authentic Certificate Double Border ── */}
      <div
        style={{
          border: "1.5px solid #000000",
          padding: "16px 20px 12px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxSizing: "border-box",
          position: "relative",
          background: "#ffffff",
        }}
      >
        {/* ══════════════ TOP HEADER SECTION ══════════════ */}
        <div>
          {/* Header row: Logo + Centered School Info + Session Badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "14px",
              marginBottom: "8px",
            }}
          >
            {/* Left: Institute Logo */}
            <div
              style={{
                width: "78px",
                height: "78px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1.5px solid #000000",
                borderRadius: "6px",
                padding: "4px",
                background: "#ffffff",
                boxSizing: "border-box",
              }}
            >
              <img
                src={instituteLogo}
                alt="Institute Logo"
                crossOrigin="anonymous"
                style={{
                  maxHeight: "68px",
                  maxWidth: "68px",
                  objectFit: "contain",
                  filter: "grayscale(100%)",
                }}
              />
            </div>

            {/* Center: School Branding */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <h1
                style={{
                  fontSize: "23px",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  margin: 0,
                  fontFamily: "'Times New Roman', Times, Georgia, serif",
                  color: "#000000",
                  lineHeight: 1.15,
                }}
              >
                {instituteName}
              </h1>

              {instituteTagline && (
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "2px",
                    margin: "3px 0 2px",
                    color: "#111111",
                  }}
                >
                  ★ {instituteTagline} ★
                </div>
              )}

              {instituteAddress && (
                <div
                  style={{
                    fontSize: "9.5px",
                    fontWeight: 600,
                    color: "#333333",
                    margin: "1px auto 0",
                    maxWidth: "94%",
                    lineHeight: 1.3,
                  }}
                >
                  {instituteAddress}
                </div>
              )}
            </div>

            {/* Right: Academic Session Badge for Balance */}
            <div
              style={{
                width: "78px",
                height: "78px",
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "1.5px solid #000000",
                borderRadius: "6px",
                padding: "4px",
                background: "#f9f9f9",
                boxSizing: "border-box",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "7.5px",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#444",
                }}
              >
                SESSION
              </div>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  margin: "2px 0",
                }}
              >
                {periodLabel || "2026-27"}
              </div>
            </div>
          </div>

          {/* Double Separator Line */}
          <div style={{ borderTop: "3px double #000000", margin: "8px 0 10px" }} />

          {/* ── Graphic Title Banner ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              marginBottom: "10px",
            }}
          >
            <div style={{ flex: 1, height: "1.5px", background: "#000000" }} />
            <div
              style={{
                background: "#000000",
                color: "#ffffff",
                padding: "5px 24px",
                fontSize: "13px",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "2.5px",
                borderRadius: "3px",
              }}
            >
              {examinations.length === 1
                ? `${examinations[0].testType} EXAMINATION RESULT`
                : "ANNUAL EXAMINATION RESULT"}
            </div>
            <div style={{ flex: 1, height: "1.5px", background: "#000000" }} />
          </div>

          {/* ── Student Information Grid ── */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginBottom: "10px",
              border: "1.5px solid #000000",
            }}
          >
            <thead>
              <tr style={{ background: "#000000", color: "#ffffff" }}>
                <th style={{ width: group ? "10%" : "12%", padding: "5.5px 8px", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", borderRight: "1px solid #444" }}>
                  STD
                </th>
                <th style={{ width: group ? "48%" : "56%", padding: "5.5px 12px", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textAlign: "left", borderRight: "1px solid #444" }}>
                  STUDENT NAME
                </th>
                <th style={{ width: group ? "16%" : "18%", padding: "5.5px 8px", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", borderRight: "1px solid #444" }}>
                  G.R. NO.
                </th>
                <th style={{ width: group ? "14%" : "14%", padding: "5.5px 8px", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", borderRight: group ? "1px solid #444" : "none" }}>
                  ROLL NO.
                </th>
                {group && (
                  <th style={{ width: "12%", padding: "5.5px 8px", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px" }}>
                    GROUP
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: "#ffffff" }}>
                <td style={{ padding: "6.5px 8px", textAlign: "center", fontSize: "12px", fontWeight: 800, borderRight: "1px solid #000" }}>
                  {std}
                </td>
                <td style={{ padding: "6.5px 12px", textAlign: "left", fontSize: "12.5px", fontWeight: 800, textTransform: "uppercase", borderRight: "1px solid #000" }}>
                  {studentName || "—"}
                </td>
                <td style={{ padding: "6.5px 8px", textAlign: "center", fontSize: "12px", fontWeight: 800, borderRight: "1px solid #000" }}>
                  {referenceId || "—"}
                </td>
                <td style={{ padding: "6.5px 8px", textAlign: "center", fontSize: "12px", fontWeight: 800, borderRight: group ? "1px solid #000" : "none" }}>
                  {rollNo || "—"}
                </td>
                {group && (
                  <td style={{ padding: "6.5px 8px", textAlign: "center", fontSize: "12px", fontWeight: 800 }}>
                    {group}
                  </td>
                )}
              </tr>
            </tbody>
          </table>

          {/* ── Main Subject-wise Marks Table (Generously spaced) ── */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginBottom: "10px",
              border: "2px solid #000000",
            }}
          >
            <thead>
              <tr style={{ background: "#111111", color: "#ffffff" }}>
                <th style={{ width: "7%", padding: "8px 6px", fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.5px", border: "1px solid #333", textAlign: "center" }}>
                  NO.
                </th>
                <th style={{ width: hasGrades ? "45%" : "55%", padding: "8px 12px", fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.5px", border: "1px solid #333", textAlign: "left" }}>
                  SUBJECT NAME
                </th>
                <th style={{ width: "16%", padding: "8px 8px", fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.5px", border: "1px solid #333", textAlign: "center" }}>
                  MAX MARKS
                </th>
                <th style={{ width: "16%", padding: "8px 8px", fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.5px", border: "1px solid #333", textAlign: "center" }}>
                  OBTAINED MARKS
                </th>
                {hasGrades && (
                  <th style={{ width: "16%", padding: "8px 8px", fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.5px", border: "1px solid #333", textAlign: "center" }}>
                    GRADE
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {subjectsList.map((subj, idx) => {
                const isAbsent = subj.absent === true;
                const pct = subj.percentage ?? 0;
                const isZebra = idx % 2 === 1;
                return (
                  <tr key={idx} style={{ background: isZebra ? "#f8f8f8" : "#ffffff" }}>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, fontSize: "11.5px", border: "1px solid #000" }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: "12px", textTransform: "uppercase", border: "1px solid #000" }}>
                      {subj.className}
                    </td>
                    <td style={{ padding: "8px 8px", textAlign: "center", fontWeight: 600, fontSize: "11.5px", border: "1px solid #000" }}>
                      {subj.maxScore ?? "—"}
                    </td>
                    <td style={{ padding: "8px 8px", textAlign: "center", fontWeight: 900, fontSize: "12.5px", border: "1px solid #000" }}>
                      {isAbsent ? (
                        <span style={{ fontStyle: "italic", fontWeight: 900 }}>AB</span>
                      ) : typeof subj.score === "number" ? (
                        Math.round(subj.score * 10) / 10
                      ) : (
                        "—"
                      )}
                    </td>
                    {hasGrades && (
                      <td style={{ padding: "8px 8px", textAlign: "center", fontWeight: 900, fontSize: "12px", border: "1px solid #000" }}>
                        {isAbsent ? "AB" : getGrade(pct, gradeLetterMapping).grade}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: "#e8e8e8", borderTop: "2px solid #000000", borderBottom: "2px solid #000000" }}>
                <td colSpan={2} style={{ padding: "8px 12px", textAlign: "center", fontWeight: 900, fontSize: "11.5px", letterSpacing: "1.5px", textTransform: "uppercase", border: "1.5px solid #000" }}>
                  TOTAL MARKS
                </td>
                <td style={{ padding: "8px 8px", textAlign: "center", fontWeight: 900, fontSize: "12.5px", border: "1.5px solid #000" }}>
                  {totalMaxMarks}
                </td>
                <td style={{ padding: "8px 8px", textAlign: "center", fontWeight: 900, fontSize: "13px", border: "1.5px solid #000" }}>
                  {typeof totalObtainedMarks === "number" ? Math.round(totalObtainedMarks * 10) / 10 : "—"}
                </td>
                {hasGrades && (
                  <td style={{ padding: "8px 8px", textAlign: "center", fontWeight: 900, fontSize: "13px", border: "1.5px solid #000" }}>
                    {overallGradeObj.grade}
                  </td>
                )}
              </tr>
            </tfoot>
          </table>

          {/* ── 4 Uniform Academic KPI Summary Cards ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "8px",
              marginBottom: "12px",
            }}
          >
            <div style={{ border: "1.5px solid #000", padding: "7px 8px", textAlign: "center", background: "#fbfbfb", borderRadius: "3px" }}>
              <div style={{ fontSize: "9px", fontWeight: 800, textTransform: "uppercase", color: "#444", letterSpacing: "0.5px" }}>TOTAL MARKS</div>
              <div style={{ fontSize: "14.5px", fontWeight: 900, margin: "2px 0 0" }}>{totalObtainedMarks} / {totalMaxMarks}</div>
            </div>
            <div style={{ border: "1.5px solid #000", padding: "7px 8px", textAlign: "center", background: "#fbfbfb", borderRadius: "3px" }}>
              <div style={{ fontSize: "9px", fontWeight: 800, textTransform: "uppercase", color: "#444", letterSpacing: "0.5px" }}>PERCENTAGE</div>
              <div style={{ fontSize: "14.5px", fontWeight: 900, margin: "2px 0 0" }}>{Math.round(overallPct * 10) / 10}%</div>
            </div>
            <div style={{ border: "1.5px solid #000", padding: "7px 8px", textAlign: "center", background: "#fbfbfb", borderRadius: "3px" }}>
              <div style={{ fontSize: "9px", fontWeight: 800, textTransform: "uppercase", color: "#444", letterSpacing: "0.5px" }}>RESULT</div>
              <div style={{ fontSize: "14.5px", fontWeight: 900, margin: "2px 0 0", letterSpacing: "1px" }}>{resultText}</div>
            </div>
            <div style={{ border: "1.5px solid #000", padding: "7px 8px", textAlign: "center", background: "#fbfbfb", borderRadius: "3px" }}>
              <div style={{ fontSize: "9px", fontWeight: 800, textTransform: "uppercase", color: "#444", letterSpacing: "0.5px" }}>OVERALL GRADE</div>
              <div style={{ fontSize: "14.5px", fontWeight: 900, margin: "2px 0 0" }}>{overallGradeObj.grade}</div>
            </div>
          </div>

          {/* ── Structured Classification of Performance & Grades Matrix (Full-Width with proper spacing) ── */}
          <div
            style={{
              border: "1.5px solid #000000",
              borderRadius: "3px",
              background: "#ffffff",
              overflow: "hidden",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                fontSize: "10px",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "1px",
                background: "#000000",
                color: "#ffffff",
                padding: "4.5px 8px",
                textAlign: "center",
              }}
            >
              CLASSIFICATION OF PERFORMANCE & GRADES
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
              <thead>
                <tr style={{ background: "#f0f0f0" }}>
                  <th style={{ width: "20%", padding: "4.5px 6px", border: "1px solid #000", textAlign: "center", fontWeight: 800 }}>GRADE</th>
                  <th style={{ width: "35%", padding: "4.5px 6px", border: "1px solid #000", textAlign: "center", fontWeight: 800 }}>PERCENTAGE RANGE</th>
                  <th style={{ width: "45%", padding: "4.5px 8px", border: "1px solid #000", textAlign: "center", fontWeight: 800 }}>PERFORMANCE REMARKS</th>
                </tr>
              </thead>
              <tbody>
                {[...effectiveGradeScale]
                  .sort((a, b) => b.minPercentage - a.minPercentage)
                  .map((g, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 1 ? "#f9f9f9" : "#ffffff" }}>
                      <td style={{ padding: "4px 6px", border: "1px solid #000", textAlign: "center", fontWeight: 800, fontSize: "10.5px" }}>
                        {g.grade}
                      </td>
                      <td style={{ padding: "4px 6px", border: "1px solid #000", textAlign: "center", fontWeight: 600 }}>
                        {g.minPercentage}% & UP
                      </td>
                      <td style={{ padding: "4px 8px", border: "1px solid #000", textAlign: "center", fontWeight: 600 }}>
                        {g.remark || "Qualified"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ══════════════ BOTTOM SIGNATURES & BRANDING SECTION ══════════════ */}
        <div style={{ marginTop: "4px" }}>
          {/* Signatures Row */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              padding: "0 10px 4px",
            }}
          >
            {/* Date */}
            <div style={{ textAlign: "left", width: "160px" }}>
              <div style={{ fontSize: "10.5px", fontWeight: 800 }}>
                DATE: <span style={{ fontWeight: 800 }}>{formattedDate}</span>
              </div>
            </div>

            {/* Class Teacher Sign */}
            <div style={{ textAlign: "center", width: "170px" }}>
              <div style={{ width: "100%", borderBottom: "1.5px solid #000000", marginBottom: "4px" }} />
              <div style={{ fontSize: "10px", fontWeight: 800, textTransform: "uppercase" }}>
                CLASS TEACHER
              </div>
            </div>

            {/* Principal Sign */}
            <div style={{ textAlign: "center", width: "170px" }}>
              <div style={{ width: "100%", borderBottom: "1.5px solid #000000", marginBottom: "4px" }} />
              <div style={{ fontSize: "10px", fontWeight: 800, textTransform: "uppercase" }}>
                PRINCIPAL / HEADMASTER
              </div>
            </div>
          </div>

          {/* Discreet Official GradeMe AI Branding Footer */}
          <div
            style={{
              marginTop: "8px",
              paddingTop: "4px",
              borderTop: "1px dashed #999999",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "8px",
              fontWeight: 700,
              color: "#555555",
              letterSpacing: "0.8px",
              textTransform: "uppercase",
            }}
          >
            <span style={{ fontWeight: 800, color: "#111111" }}>
              Powered by GradeMe AI
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GJBWReportCard;
